import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { useAuth } from '../contexts/AuthContext';

const DEFAULT_PAGE_SIZE = 10;

const getCoachId = () => {
  try {
    const stored = JSON.parse(localStorage.getItem('user') || '{}');
    return stored?.customerId + "-" + stored?.userId;
  } catch (error) {
    console.error('Failed to parse localStorage user:', error);
    return undefined;
  }
};

const useCoachCourses = () => {
  const { apiClient } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
  });

  const coachId = getCoachId();

  const fetchCourses = useCallback(
    async (pageNo = 1, pageSize = DEFAULT_PAGE_SIZE) => {
      if (!coachId) {
        message.warning('未找到教练编号，无法查询课程');
        return;
      }

      setLoading(true);
      try {
        const response = await apiClient.post('/sports-server-prod/taller.api.v1/coachCourse/pageList', {
          coach: coachId,
          pageNo,
          pageSize,
        });

        if (response.data?.code === 200) {
          const list = response.data?.data?.list || [];
          setCourses(list);
          setPagination({
            current: pageNo,
            pageSize,
            total: response.data?.data?.totalRecord || 0,
          });
        } else {
          message.error(response.data?.msg || '获取我的课程失败');
          setCourses([]);
        }
      } catch (error) {
        console.error('fetch coach courses error:', error);
        message.error('获取我的课程失败');
        setCourses([]);
      } finally {
        setLoading(false);
      }
    },
    [apiClient, coachId]
  );

  useEffect(() => {
    fetchCourses(1, DEFAULT_PAGE_SIZE);
  }, [fetchCourses]);

  const addCourse = useCallback(
    async ({ courseDesc, coursePrice, relateAction, relateActionShow }) => {
      if (!coachId) {
        message.warning('未找到教练编号，无法添加课程');
        return;
      }

      try {
        const response = await apiClient.post('/sports-server-prod/taller.api.v1/coachCourse/add', {
          courseDesc,
          coursePrice,
          coach: coachId,
          relateAction, // 存储seq数值，用逗号连接
          relateActionShow // 展示eventTitle文字，用逗号连接
        });

        if (response.data?.code === 200) {
          message.success(response.data?.msg || '添加成功');
          await fetchCourses(1, pagination.pageSize);
        } else {
          message.error(response.data?.msg || '添加课程失败');
        }
      } catch (error) {
        console.error('add coach course error:', error);
        message.error('添加课程失败');
      }
    },
    [apiClient, coachId, fetchCourses, pagination.pageSize]
  );

  const updateCourse = useCallback(
    async ({ id, courseDesc, coursePrice, relateAction, relateActionShow }) => {
      if (!coachId) {
        message.warning('未找到教练编号，无法更新课程');
        return;
      }

      try {
        const response = await apiClient.post('/sports-server-prod/taller.api.v1/coachCourse/add', {
          id,
          courseDesc,
          coursePrice,
          coach: coachId,
          relateAction, // 存储seq数值，用逗号连接
          relateActionShow // 展示eventTitle文字，用逗号连接
        });

        if (response.data?.code === 200) {
          message.success(response.data?.msg || '更新成功');
          await fetchCourses(pagination.current, pagination.pageSize);
        } else {
          message.error(response.data?.msg || '更新课程失败');
        }
      } catch (error) {
        console.error('update coach course error:', error);
        message.error('更新课程失败');
      }
    },
    [apiClient, coachId, fetchCourses, pagination.current, pagination.pageSize]
  );

  const deleteCourse = useCallback(
    async (courseId) => {
      try {
        const response = await apiClient.post('/sports-server-prod/taller.api.v1/coachCourse/del', { id: courseId });

        if (response.data?.code === 200) {
          message.success(response.data?.msg || '删除成功');
          const remaining = courses.length - 1;
          const nextPage = remaining > 0 ? pagination.current : Math.max(1, pagination.current - 1);
          await fetchCourses(nextPage, pagination.pageSize);
        } else {
          message.error(response.data?.msg || '删除课程失败');
        }
      } catch (error) {
        console.error('delete coach course error:', error);
        message.error('删除课程失败');
      }
    },
    [apiClient, courses.length, fetchCourses, pagination.current, pagination.pageSize]
  );

  return {
    courses,
    loading,
    pagination,
    fetchCourses,
    addCourse,
    updateCourse,
    deleteCourse,
  };
};

export default useCoachCourses;